import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';

import { FiArrowRight, FiChevronDown, FiMenu, FiX } from 'react-icons/fi';
import {
  FaChartLine,
  FaShieldHalved as FaShieldAlt,
  FaMobileScreenButton as FaMobileAlt,
  FaArrowsRotate as FaSyncAlt,
  FaUsers,
  FaGear as FaCog,
  FaStar,
  FaLinkedinIn,
  FaInstagram,
  FaFacebookF,
  FaXTwitter,
  FaYoutube,
} from 'react-icons/fa6';
import { MdDashboard } from 'react-icons/md';
import { useAuth } from '../context/AuthContext';
import { useRemoteCss } from '../hooks/useRemoteCss';
import axiosInstance from '../api/axiosInstance';
import CookieBanner from '../components/CookieBanner';
import { useCookieConsent } from '../hooks/useCookieConsent';
import AIChatWidget from '../components/AIChatWidget';

import Slider from "react-slick";
//import "slick-carousel/slick/slick.css";
//import "slick-carousel/slick/slick-theme.css";

import ThemeDock from '../components/public/ThemeDock';
import PageLoader from '../components/public/PageLoader';
import useAppTheme from '../hooks/useAppTheme';
import './cx.css';
import './chrome.css';
import './landing.css';

/* ───────── tiny loader ───────── */
const StarRating = ({ value = 5 }) => (
  <div className="stars" aria-label={`${value} star rating`}>
    {[...Array(5)].map((_, i) => (
      <FaStar key={i} className={`star ${i < value ? 'filled' : ''}`} />
    ))}
  </div>
);

const Avatar = ({ src, name, size = 36, className = '' }) => {
  const [err, setErr] = React.useState(false);
  const initials = (name || '')
    .split(' ')
    .map((n) => n[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();
  const style = { width: size, height: size, fontSize: Math.max(10, size * 0.35) };
  return err || !src ? (
    <div className={`avatar fallback ${className}`} style={style} aria-label={name}>
      {initials || '👤'}
    </div>
  ) : (
    <img
      src={src}
      alt={name}
      className={`avatar ${className}`}
      style={style}
      width={size}
      height={size}
      loading="lazy"
      onError={() => setErr(true)}
    />
  );
};

/* Floating Theme Dock (center of screen, left or right) */
export default function LandingPage() {
  const { authData } = useAuth();
  const { consent } = useCookieConsent();

  // brown / green, saved in localStorage and applied to <html data-theme>
  const { theme, setTheme } = useAppTheme();

  // optional remote css
  const cssUrl = `${axiosInstance.defaults.baseURL}/styles/LandingPage`;
  const cssReady = useRemoteCss(cssUrl);

  const navigate = useNavigate();
  const goToRegister = () => navigate('/register');
  const goToLogin = () => navigate('/login');

  /* SEO Meta Tags */
  useEffect(() => {
    document.title = 'Nnetego | Hotel Management System';
    const metaDescription = document.createElement('meta');
    metaDescription.name = 'description';
    metaDescription.content =
      'All-in-one hotel management platform that simplifies operations, enhances guest experiences, and boosts revenue.';
    document.head.appendChild(metaDescription);

    const metaKeywords = document.createElement('meta');
    metaKeywords.name = 'keywords';
    metaKeywords.content =
      'hotel management system, property management software, PMS, hotel technology, hospitality solutions, hotel operations';
    document.head.appendChild(metaKeywords);

    return () => {
      document.head.removeChild(metaDescription);
      document.head.removeChild(metaKeywords);
    };
  }, []);

  // demo trigger for chat widget
  const [demoTrigger, setDemoTrigger] = useState(null);
  const [showChat, setShowChat] = useState(false);

  // minimum delay so the loader doesn't flicker
  const [minDelayDone, setMinDelayDone] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setMinDelayDone(true), 600);
    return () => clearTimeout(t);
  }, []);

  // header state + mobile menu
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  useEffect(() => setMenuOpen(false), [location.pathname]);

  // ===== NAV items (adds How it Works as external PDF) =====
  const NAV = useMemo(
    () => [
      { id: 'home', label: 'Home' },
      { id: 'about', label: 'About Us' },
      { id: 'features', label: 'Features' },
      // { id: 'testimonials', label: 'Testimonials' },
      { label: 'Support', route: '/support' },
      { label: 'How it Works', href: '/docs/how-it-works.pdf' }, // external; no id
    ],
    [],
  );

  // ------ active section for scroll-spy (must be declared BEFORE the effect) ------
  const [active, setActive] = useState('home');

  // Single scroll-spy effect (ignores external PDF item)
  useEffect(() => {
    const sections = NAV
      .filter((n) => n.id && !n.href)
      .map((n) => document.getElementById(n.id))
      .filter(Boolean);
    if (!sections.length) return;

    const obs = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (visible?.target?.id) setActive(visible.target.id);
      },
      { rootMargin: '-40% 0px -55% 0px', threshold: [0.15, 0.4, 0.7] },
    );
    sections.forEach((s) => obs.observe(s));
    return () => obs.disconnect();
  }, [NAV]);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setMenuOpen(false);
  };

  // Nav click handler: in-page scroll OR open PDF in new tab
  // Nav click handler: in-page scroll OR open PDF in new tab OR internal route
const handleNavClick = (item) => {
  if (item.route) {
    navigate(item.route);
    setMenuOpen(false);
    return;
  }

  if (item.href) {
    window.open(item.href, '_blank', 'noopener,noreferrer');
    setMenuOpen(false);
    return;
  }

  scrollTo(item.id);
};


  if (!cssReady || !minDelayDone) return <PageLoader />;

  /* ───────── Content data ───────── */
  const features = [
    {
      icon: <FaCog />,
      title: 'Front Desk+',
      description: 'Check-in/out, folios, late checkout, and incident notes.',
    },
    {
      icon: <FaUsers />,
      title: 'Rooms & Rates',
      description: 'Room types, packages, and dynamic pricing—no spreadsheets.',
    },
    { icon: <FaShieldAlt />, title: 'Housekeeping', description: 'Live room status and priorities.' },
    { icon: <FaMobileAlt />, title: 'Mobile Orders', description: 'QR menus, ticket routing, posts to guest folio.' },
    {
      icon: <FaChartLine />,
      title: 'POS & Restaurant',
      description: 'Unified bills; post to room, split checks, track KOT.',
    },
    {
      icon: <FaSyncAlt />,
      title: 'Cloud First',
      description: '24/7 availability—backups and updates handled for you.',
    },
  ];
  const stats = [
    { number: '99.9%', label: 'Uptime SLA' },
    { number: '50+', label: 'Integrations' },
    { number: '5 min', label: 'Average Setup' },
    { number: '24/7', label: 'Support' },
  ];
  const testimonials = [
    {
      text: 'Switched in a weekend. Front Desk+ and QR ordering made ops smoother from day one.',
      name: 'Naledi K.',
      role: 'GM, City Boutique',
      avatar: '/images/avatars/1.jpg',
      rating: 5,
    },
    {
      text: 'Housekeeping priorities and live room status cut our turnaround time by 30%.',
      name: 'Mark D.',
      role: 'Ops Manager, Riverside Inn',
      avatar: '/images/avatars/2.jpg',
      rating: 5,
    },
    {
      text: 'Unified folios and KOT tracking finally aligned our restaurant and rooms revenue.',
      name: 'Aisha M.',
      role: 'F&B Lead, Airport Suites',
      avatar: '/images/avatars/3.jpg',
      rating: 5,
    },
  ];


  const heroSlides = [
  { src: "/images/hero/hms-1.png", alt: "Front Desk dashboard overview" },
  //{ src: "/images/hero/hms-2.png", alt: "Housekeeping live room status board" },
  //{ src: "/images/hero/hms-3.png", alt: "POS and restaurant workflow" },
];

const prefersReducedMotion =
  typeof window !== "undefined" &&
  window.matchMedia &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const heroSliderSettings = {
  dots: false,
  arrows: false,
  infinite: true,
  speed: 800,
  slidesToShow: 1,
  slidesToScroll: 1,
  autoplay: !prefersReducedMotion,
  autoplaySpeed: 4500,
  fade: true,
  pauseOnHover: false,
  pauseOnFocus: false,
  swipe: false,
  draggable: false,
};


////

const heroBgSlides = [
  //"/images/Wild.webp",
  "/images/login-background.png",
  "/images/hero/cover-1.webp",
];
 
const heroBgSettings = {
  dots: false,
  arrows: false,
  infinite: true,
  speed: 1200,
  slidesToShow: 1,
  slidesToScroll: 1,
  autoplay: !prefersReducedMotion,
  autoplaySpeed: 6000,
  fade: true,
  pauseOnHover: false,
  pauseOnFocus: false,
  swipe: false,
  draggable: false,
};

  
  return (
    <div className="lux-layout">
      {/* Floating theme dock (mid-screen) */}
      <ThemeDock theme={theme} setTheme={setTheme} />

      {/* Topbar */}
      <div className="lux-topbar">Explore our newest modules &amp; integrations.</div>

      {/* ========== HEADER ========== */}
      <header className={`lux-header ${scrolled ? 'is-scrolled' : ''} ${menuOpen ? 'menu-open' : ''}`}>
        <div className="lux-container header-inner">
          <div className="lux-logo" onClick={() => scrollTo('home')} role="button" tabIndex={0}>
            <img src="/images/logo.png" alt="Nnetego" style={{ height: 28 }} />
          </div>

          {/* Nav: buttons only, preserves styling; external PDF just opens in new tab */}
          <nav className={`lux-menu ${menuOpen ? 'open' : ''}`}>
            {NAV.map((item) => (
              <button
                key={item.id || item.label}
                className={`lux-nav-link ${!item.href && active === item.id ? 'active' : ''}`}
                onClick={() => handleNavClick(item)}
              >
                {item.label}
              </button>
            ))}
          </nav>

          <div className="header-right">
            {authData?.token ? (
              <button className="lux-cta" onClick={() => navigate('/dashboard')}>
                <MdDashboard style={{ marginRight: 8 }} /> Dashboard
              </button>
            ) : (
              <button className="lux-cta" onClick={goToLogin}>
                Get Started
              </button>
            )}
            <button
              className="lux-mobile-toggle"
              onClick={() => setMenuOpen((v) => !v)}
              aria-label="Toggle menu"
            >
              {menuOpen ? <FiX /> : <FiMenu />}
            </button>
          </div>
        </div>
      </header>



      {/* ========== HERO (premium full-screen) ========== */}
      <section id="home" className="lux-hero">
        <div className="lux-hero-bg" aria-hidden="true">
          <Slider {...heroBgSettings}>
            {heroBgSlides.map((src) => (
              <div key={src} className="lux-hero-bg-slide">
                <div className="lux-hero-bg-img" style={{ backgroundImage: `url(${src})` }} />
              </div>
            ))}
          </Slider>

          {/* keep your darkening / blend layer */}
          <div className="lux-hero-bg-overlay" aria-hidden="true" />
        </div>


        <div className="lux-container hero-grid">
          {/* LEFT: copy / CTAs */}
          <div className="hero-left">
            <div className="hero-pill-row">
              <span className="brand-tag">Hotel Management Solution</span>
              <span className="hero-pill">Built for modern hospitality</span>
            </div>

            <h1 className="lux-hero-title">
              Attention to detail <span className="thin">in</span> <em>property management</em>
            </h1>

            <p className="lux-hero-sub">
              Run front desk, housekeeping, POS, and mobile orders from a single, calm dashboard—without spreadsheets
              or clashing systems.
            </p>

            <div className="hero-cta-row">
              
              
              <button
  className="lux-cta"
  onClick={() => {
    setShowChat(true);
    setDemoTrigger((n) => (typeof n === "number" ? n + 1 : 1));
  }}
>
  Request Demo <FiArrowRight />
</button>


              <button className="lux-btn-ghost" onClick={goToRegister}>
                Start Free Trial
              </button>
            </div>

            <p className="hero-meta">No long contracts · Go live instantly · Local support</p>
          </div>

          {/* RIGHT: flat preview card with video */}
          <div className="hero-right">
            <div className="hero-card">
              <div className="hero-card-header">
                <span className="hero-card-dot" />
                <span className="hero-card-dot" />
                <span className="hero-card-dot" />
                <span className="hero-card-title">HMS in action</span>
              </div>
              {/*
              <div className="hero-video-frame">
                <video
                  className="hero-video-el"
                  autoPlay
                  loop
                  muted
                  playsInline
                  preload="auto"
                  poster="/images/Vids/HMSVid-poster.jpg"
                  title="Hotel Management System preview"
                >
                  <source src="/images/Vids/HMSVid.mp4" type="video/mp4" />
                  <source src="/images/Vids/HMSVid.webm" type="video/webm" />
                  Your browser does not support the video tag.
                </video>
              </div>
              */}

              <div className="hero-video-frame" aria-label="Hotel Management System preview slideshow">
                <Slider {...heroSliderSettings}>
                  {heroSlides.map((s, idx) => (
                    <div key={s.src} className="hero-slide">
                      <img
                        className="hero-video-el"
                        src={s.src}
                        alt={s.alt}
                        loading={idx === 0 ? "eager" : "lazy"}
                        decoding="async"
                      />
                    </div>
                  ))}
                </Slider>

                {/* Optional: keep if you had a dark overlay for text readability */}
                <div className="hero-media-overlay" aria-hidden="true" />
              </div>


              <div className="hero-card-footer">
                <div className="hero-foot-stat">
                  <span className="hero-foot-label">Front Desk+</span>
                  <span className="hero-foot-value">Check-in in 3 clicks</span>
                </div>
                <div className="hero-foot-stat">
                  <span className="hero-foot-label">Housekeeping</span>
                  <span className="hero-foot-value">Live room status</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <button
          type="button"
          className="scroll-down-button"
          onClick={() => scrollTo('about')}
          aria-label="Scroll down to learn more"
        >
          <span className="scroll-down-label">Discover</span>
          <span className="scroll-down-orb">
            <FiChevronDown size={20} />
          </span>
        </button>

      </section>

      {/* ========== ABOUT ========== */}
      <section id="about" className="lux-section lux-section--ivory">
        <div className="lux-container">
          <div className="lux-section-head">
            <h2 className="lux-h2">Welcome to Nnetego</h2>
          </div>
          <div className="about-grid">
            <div className="about-copy">
              <p className="lux-copy">
                Experience the blend of contemporary technology and timeless hospitality. Our platform is crafted to
                serve boutique hotels and groups alike—with performance and simplicity at the core.
              </p>
              <p className="lux-copy">
                From Front Desk+ to pure mobile ordering, every module is designed so that <em>Everything Matters</em>.
              </p>
              <button className="lux-cta" onClick={goToRegister}>
                Start Free Trial
              </button>
            </div>
            <div
              className="lux-media lux-media--landscape"
              style={{ backgroundImage: `url(/images/login-background.png)` }}
            />  
              
               
          </div>
        </div>
      </section>

      {/* ========== FEATURES ========== */}
      <section id="features" className="lux-section lux-section--ivory">
        <div className="lux-container">
          <div className="lux-section-head">
            <h2 className="lux-h2">What Sets Us Apart</h2>
            <p className="lux-copy">Quiet, elegant utility—built for modern hospitality.</p>
          </div>

          <div className="feature-pills">
            {features.map((f, i) => (
              <div key={i} className="feature-pill">
                <div className="pill-icon" aria-hidden="true">
                  {f.icon}
                </div>
                <div className="pill-text">
                  <strong>{f.title}</strong>
                  <small>{f.description}</small>
                </div>
              </div>
            ))}
          </div>
        </div>

      </section>

      {/* ========== STATS ========== */}
      <section className="stats-section">
        <div className="lux-container stats-wrap">
          {stats.map((s, i) => (
            <div key={i} className="stat-item">
              <div className="stat-number">{s.number}</div>
              <div className="stat-label">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ========== TESTIMONIALS ========== */}
      <section
        id="testimonials"
        className="lux-section lux-section--ivory"
        itemScope
        itemType="https://schema.org/Review"
      >
        <div className="lux-container">
          <div className="lux-section-head center">
            <div className="section-eyebrow">Testimonials</div>
            <h2 className="lux-h2">Trusted by Hoteliers</h2>
            <p className="lux-copy">Quiet confidence from real operators.</p>
          </div>

          <div className="tst-grid">
            {testimonials.map((t, i) => (
              <article key={i} className="tst-card" itemScope itemType="https://schema.org/Review">
                <StarRating value={t.rating || 5} />
                <p className="tst-quote" itemProp="reviewBody">
                  {t.text}
                </p>
                <div className="tst-meta" itemProp="author" itemScope itemType="https://schema.org/Person">
                  <Avatar src={t.avatar} name={t.name} size={48} />
                  <div>
                    <div className="tst-name" itemProp="name">
                      {t.name}
                    </div>
                    <div className="tst-role" itemProp="jobTitle">
                      {t.role}
                    </div>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>

      </section>

      {/* ========== CTA BANNER ========== */}
      <section className="cta-section">
        <div className="cta-background" style={{ backgroundImage: `url('/images/bck.jpg')` }}>
          <div className="cta-overlay">
            <div className="lux-container">
              <div className="cta-content">
                <h2 className="cta-title">Discover Our Signature Work</h2>
                <p className="cta-text">
                  We aim for the very highest standards—because Everything Matters.
                </p>
                <div className="cta-actions">
                  <button className="lux-cta" onClick={goToRegister}>
                    Start Free Trial
                  </button>
                  <a className="lux-btn-ghost" href="/contact">
                    Contact Us
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ========== FOOTER ========== */}
      <footer id="contact" className="lux-footer">
        <div className="lux-container">
          <div className="footer-top">
            <div className="lux-logo lux-logo--footer">
              <img src="/images/logo.png" alt="Nnetego" style={{ height: 28 }} />
            </div>
            <p className="lux-footline" style={{ maxWidth: 480 }}>
              We build modern property-management solutions that help hotels deliver exceptional guest experiences
              while optimizing operations.
            </p>
          </div>

          <div className="footer-columns">
            <div className="foot-col">
              <h4 className="lux-foothead">Head Office</h4>
              <div className="lux-footline">Plot 22211, Nshaiwa Street, Phase 4</div>
              <div className="lux-footline">Gaborone, Botswana</div>
            </div>
            <div className="foot-col">
              <h4 className="lux-foothead">Phone</h4>
              <div className="lux-footline">
                <a href="tel:+26776350228">+267 76 350 228</a>
              </div>
              <div className="lux-footline">Mon–Fri, 8am–5pm CAT</div>
            </div>
            <div className="foot-col">
              <h4 className="lux-foothead">Connect</h4>
              <div className="lux-social">
                <a href="#">
                  <FaLinkedinIn />
                </a>
                <a href="#">
                  <FaInstagram />
                </a>
                <a href="#">
                  <FaFacebookF />
                </a>
                <a href="#">
                  <FaXTwitter />
                </a>
                <a href="#">
                  <FaYoutube />
                </a>
              </div>
            </div>
          </div>

          <hr className="foot-sep" />

          {/* Refined legal/credits row */}
        

            <div className="lux-legal">
              <div className="lux-legal-copy">
                © {new Date().getFullYear()} Nnetego. All rights reserved.{" "}
                <Link to="/legal/privacy">Privacy</Link> • <Link to="/legal/terms">Terms</Link>
              </div>

              <div className="lux-legal-credits">
                Designed by{" "}
                <a href="https://lanesands.com" target="_blank" rel="noopener noreferrer">
                  Lanesands
                </a>
                .
              </div>
            </div>

        </div>

        {consent?.functional && showChat && (
          <AIChatWidget
            startOpen={false}
            openSignal={demoTrigger}
            startQuoteOnOpen
            brandColor={theme === "green" ? "#16a34a" : "#b89b6e"}
            title="Online Assistant"
            subtitle="Let’s get you a quick demo quote"
          />
        )}

      </footer>

      <CookieBanner />
    </div>
  );
}
